/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
import UserService from '../../services/user';

/**
 * Vuex module for managing user preferences
 * Provides centralized storage and persistence for user-specific settings
 *
 * Preference keys should be namespaced for better organization:
 * - 'otp.threshold' - OTP dashboard threshold setting
 * - 'scheduler.{name}.columnState' - Scheduler column states
 * - 'grid.{name}.columnState' - AG Grid column states
 *
 * Use the helper functions to generate namespaced keys:
 * - createPreferenceKey('otp', 'threshold') => 'otp.threshold'
 * - createPreferenceKey('scheduler', schedulerName, 'columnState') => 'scheduler.{name}.columnState'
 */

/**
 * Helper function to create namespaced preference keys
 * @param {...string} parts - Key parts to join with dots
 * @returns {string} - Namespaced preference key
 * @example
 * createPreferenceKey('otp', 'threshold') // 'otp.threshold'
 * createPreferenceKey('scheduler', 'planning', 'columnState') // 'scheduler.planning.columnState'
 */
export function createPreferenceKey(...parts) {
  return parts.filter(Boolean).join('.');
}

const state = {
  preferences: {},
  isLoaded: false,
  loadingKeys: new Set(),
  loadingPromises: {}, // Track ongoing load promises to prevent race conditions
};

const mutations = {
  SET_PREFERENCE(state, { key, value }) {
    state.preferences = {
      ...state.preferences,
      [key]: value,
    };
  },
  SET_PREFERENCES(state, preferences) {
    state.preferences = preferences;
  },
  SET_IS_LOADED(state, isLoaded) {
    state.isLoaded = isLoaded;
  },
  ADD_LOADING_KEY(state, key) {
    // Create new Set to trigger Vue reactivity
    state.loadingKeys = new Set([...state.loadingKeys, key]);
  },
  REMOVE_LOADING_KEY(state, key) {
    // Create new Set to trigger Vue reactivity
    const newSet = new Set(state.loadingKeys);
    newSet.delete(key);
    state.loadingKeys = newSet;
  },
  SET_LOADING_PROMISE(state, { key, promise }) {
    state.loadingPromises = {
      ...state.loadingPromises,
      [key]: promise,
    };
  },
  REMOVE_LOADING_PROMISE(state, key) {
    const { [key]: removed, ...rest } = state.loadingPromises;
    state.loadingPromises = rest;
  },
  CLEAR_PREFERENCES(state) {
    state.preferences = {};
    state.isLoaded = false;
  },
};

const actions = {
  /**
   * Load a single preference from the backend
   * @param {string} key - Preference key
   * @returns {Promise<any>} - Preference value
   */
  async loadPreference({ commit, state }, key) {
    // If already loading, return the existing promise to avoid race condition
    if (state.loadingPromises[key]) {
      return state.loadingPromises[key];
    }

    // Create and store the loading promise
    const promise = (async () => {
      try {
        commit('ADD_LOADING_KEY', key);
        const value = await UserService.getPreferences(key);

        if (value !== null && value !== undefined) {
          commit('SET_PREFERENCE', { key, value });
        }

        return value;
      } catch (error) {
        console.error(`Error loading preference '${key}':`, error);
        return null;
      } finally {
        commit('REMOVE_LOADING_KEY', key);
        commit('REMOVE_LOADING_PROMISE', key);
      }
    })();

    commit('SET_LOADING_PROMISE', { key, promise });
    return promise;
  },

  /**
   * Save a preference to the backend and store
   * @param {string} key - Preference key
   * @param {any} value - Preference value
   * @returns {Promise<boolean>} - Success status
   */
  async savePreference({ commit }, { key, value }) {
    try {
      await UserService.savePreferences(key, value);
      commit('SET_PREFERENCE', { key, value });
      return true;
    } catch (error) {
      console.error(`Error saving preference '${key}':`, error);
      return false;
    }
  },

  /**
   * Get a preference value with fallback
   * Loads from backend if not in store
   * @param {string} key - Preference key
   * @param {any} defaultValue - Default value if preference not found
   * @returns {Promise<any>} - Preference value or default
   */
  async getPreference({ state, dispatch }, { key, defaultValue = null }) {
    // Check if already in store
    if (key in state.preferences) {
      return state.preferences[key];
    }

    // Load from backend
    const value = await dispatch('loadPreference', key);

    return value !== null && value !== undefined ? value : defaultValue;
  },

  /**
   * Clear all preferences from store (does not delete from backend)
   */
  clearPreferences({ commit }) {
    commit('CLEAR_PREFERENCES');
  },
};

const getters = {
  /**
   * Get a preference value from store
   * @param {string} key - Preference key
   * @returns {any} - Preference value or undefined
   */
  preference: (state) => (key) => state.preferences[key],

  /**
   * Check if a preference exists in store
   * @param {string} key - Preference key
   * @returns {boolean}
   */
  hasPreference: (state) => (key) => key in state.preferences,

  /**
   * Get all preferences
   * @returns {Object}
   */
  allPreferences: (state) => state.preferences,

  /**
   * Check if preferences are loaded
   * @returns {boolean}
   */
  isLoaded: (state) => state.isLoaded,

  /**
   * Check if a specific key is currently loading
   * @param {string} key - Preference key
   * @returns {boolean}
   */
  isLoading: (state) => (key) => state.loadingKeys.has(key),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
/* eslint-enable no-param-reassign */
